import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { inboundWebhookUrl, wordPressHubWebhookUrl } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/toast/ToastProvider';
import styles from './WordPressConnectPanel.module.css';

type Props = {
  integrationId: string;
  projectSlug: string;
  apiOrigin: string;
  siteUrl?: string | null;
  busy: boolean;
};

export function WordPressConnectPanel({
  integrationId,
  projectSlug,
  apiOrigin,
  siteUrl,
  busy,
}: Props) {
  const toast = useToast();
  const inboundUrl = inboundWebhookUrl(apiOrigin, projectSlug);
  const hubWebhookUrl = siteUrl ? wordPressHubWebhookUrl(siteUrl) : null;

  const testLead = useMutation({
    mutationFn: () =>
      apiFetch<{ message: string; email: string; lead_id: string }>(
        `/api/integrations/${integrationId}/wordpress/test-lead`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => toast.push(data.message, 'success'),
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.push(`${label} gekopieerd`, 'success');
    } catch {
      toast.push('Kopiëren mislukt', 'error');
    }
  }

  return (
    <details className={styles.details} open>
      <summary>Ploko Connect (leads + webhooks)</summary>
      <div className={styles.body}>
        <p className={styles.lead}>
          Plugin <strong>v0.2.0</strong> in <code>wordpress/ploko-connect</code>
          {siteUrl ? (
            <>
              {' '}
              voor{' '}
              <a href={siteUrl} target="_blank" rel="noreferrer">
                {siteUrl}
              </a>
            </>
          ) : null}
          . CF7/WPForms → Hub; Hub events → WP REST.
        </p>

        <h4 className={styles.sectionTitle}>WP → Hub (inbound)</h4>
        <dl className={styles.facts}>
          <div>
            <dt>Inbound URL</dt>
            <dd>
              <code className={styles.code}>{inboundUrl}</code>
              <Button type="button" variant="ghost" onClick={() => void copy(inboundUrl, 'Inbound URL')}>
                Kopieer
              </Button>
            </dd>
          </div>
        </dl>

        <h4 className={styles.sectionTitle}>Hub → WP (Fase 2)</h4>
        {hubWebhookUrl ? (
          <dl className={styles.facts}>
            <div>
              <dt>Webhook URL (Developer portal)</dt>
              <dd>
                <code className={styles.code}>{hubWebhookUrl}</code>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void copy(hubWebhookUrl, 'Webhook URL')}
                >
                  Kopieer
                </Button>
              </dd>
            </div>
          </dl>
        ) : (
          <p className={styles.hint}>Stel site URL in op de WordPress-koppeling voor de webhook-URL.</p>
        )}
        <p className={styles.hint}>
          1. <Link to="/app/developer">Developer portal</Link> → webhook → plak URL → events{' '}
          <code>lead.created</code>, <code>reply.received</code> → kopieer signing secret naar WP plugin.
          <br />
          2. API-key inbound: <Link to="/app/projects">Projecten → Integratie</Link>.
        </p>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            disabled={busy || testLead.isPending}
            onClick={() => testLead.mutate()}
          >
            Testlead in Hub
          </Button>
        </div>
      </div>
    </details>
  );
}
